import axios from 'axios';
import { attachAuth } from '@/utils/authSession';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:9000/',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

attachAuth(apiClient);

export default {
  /** UFC home payload: next event, main card, rails, champions. */
  getOverview() {
    return apiClient.get('/api/sports/mma/overview');
  },

  /**
   * Event browser. window = 'upcoming' | 'recent' | 'archive'.
   * year/page/pageSize apply to the archive window only (page is 1-based).
   */
  getEvents({ window = 'upcoming', year = null, page = null, pageSize = null } = {}) {
    const params = new URLSearchParams({ window });
    if (year != null) params.set('year', year);
    if (page != null) params.set('page', page);
    if (pageSize != null) params.set('pageSize', pageSize);
    return apiClient.get(`/api/sports/mma/events?${params.toString()}`);
  },

  /** Archive year index: { years: [{ year, eventCount }] }. */
  getEventYears() {
    return apiClient.get('/api/sports/mma/events/years');
  },

  /** Event Center: event header + segmented fight card + prediction block. */
  getEvent(eventId) {
    return apiClient.get(`/api/sports/mma/events/${eventId}`);
  },

  /** Fight Center: full pre/post-fight payload + prediction block. */
  getFight(fightId) {
    return apiClient.get(`/api/sports/mma/fights/${fightId}`);
  },

  /** Fighter profile: bio, analytics windows, full fight history. */
  getFighter(fighterId) {
    return apiClient.get(`/api/sports/mma/fighters/${fighterId}`);
  },

  /** Current divisional rankings snapshot. */
  getRankings() {
    return apiClient.get('/api/sports/mma/rankings');
  },

  /**
   * Consumer published-recommendation envelope for the next upcoming event(s).
   * Uses fight/event prediction blocks, never the ops entity list.
   */
  async getPublishedPicksEnvelope() {
    const { parsePickEnvelope, ENVELOPE_LOCKED, ENVELOPE_OK, ENVELOPE_INVALID } = await import('@/utils/parsePickEnvelope');
    const listRes = await this.getEvents({ window: 'upcoming' });
    const events = (listRes.data && listRes.data.events) || [];
    const merged = {
      status: ENVELOPE_OK,
      locked: false,
      requiredTier: 'CORE',
      picksAvailable: 0,
      picks: [],
      sport: 'MMA',
      legacy: false,
      error: null,
    };
    let sawValid = false;
    let sawInvalid = false;
    for (const event of events.slice(0, 2)) {
      const id = event && (event.eventId != null ? event.eventId : event.id);
      if (id == null) continue;
      const center = await this.getEvent(id);
      const raw = center.data && center.data.prediction;
      if (raw == null) continue;
      const parsed = parsePickEnvelope(raw);
      if (parsed.status === ENVELOPE_INVALID) {
        sawInvalid = true;
        continue;
      }
      sawValid = true;
      merged.requiredTier = parsed.requiredTier || merged.requiredTier;
      merged.picksAvailable += Number(parsed.picksAvailable || 0);
      if (parsed.locked) {
        merged.locked = true;
      } else {
        merged.picks.push(...parsed.picks);
      }
    }
    if (!sawValid && sawInvalid) {
      return {
        ...merged,
        status: ENVELOPE_INVALID,
        error: 'unrecognized_shape',
      };
    }
    if (merged.locked && merged.picks.length === 0) {
      merged.status = ENVELOPE_LOCKED;
    }
    return merged;
  },
};
